import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { HelpCircle } from 'lucide-react';
import { normalizeGrade, hasBatteryReading, GRADE_COSMETIC_NOTE } from '../constants/grades';

// ── InfoTip: kis `?` gomb + kattintásra/tapra nyíló popover ──────────────────
// A popover PORTÁLON, position:fixed-del renderelődik a <body>-ba, így SEMMILYEN
// `overflow:hidden` szülő (pl. a kép-konténer) nem vágja le – Safari desktopon is
// olvasható marad. Kívül-kattintás, Escape, görgetés és resize zárja.
export const InfoTip = ({ text, label = 'Magyarázat' }) => {
  const [open, setOpen] = useState(false);
  const tipId = React.useId();
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);
  const tipRef = useRef(null);

  const close = useCallback(() => setOpen(false), []);

  const place = useCallback(() => {
    const el = btnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const margin = 12;
    const half = 104; // a w-52 (208px) popover fele
    const left = Math.min(Math.max(r.left + r.width / 2, half + margin), window.innerWidth - half - margin);
    setPos({ left, top: r.bottom + 6 }); // lefelé nyílik – a `?` alatt mindig van hely
  }, []);

  const toggle = useCallback((e) => {
    e.stopPropagation();
    e.preventDefault();
    setOpen((o) => {
      if (!o) place();
      return !o;
    });
  }, [place]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (btnRef.current && btnRef.current.contains(e.target)) return;
      if (tipRef.current && tipRef.current.contains(e.target)) return;
      close();
    };
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('click', onDoc);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('click', onDoc);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open, close]);

  return (
    <span className="relative inline-flex">
      <button
        ref={btnRef}
        type="button"
        aria-label={label}
        aria-expanded={open}
        aria-describedby={open ? tipId : undefined}
        onClick={toggle}
        className="relative inline-flex items-center justify-center text-gray-500 hover:text-gray-700 after:absolute after:-inset-2 after:content-['']"
      >
        <HelpCircle size={15} strokeWidth={2.25} aria-hidden="true" />
      </button>
      {open && pos && createPortal(
        <span
          ref={tipRef}
          id={tipId}
          role="tooltip"
          style={{ position: 'fixed', left: pos.left, top: pos.top, transform: 'translateX(-50%)' }}
          className="z-[9999] w-52 max-w-[calc(100vw-24px)] px-3 py-2 rounded-lg bg-[#333333] text-white text-[11px] leading-snug shadow-lg text-left normal-case font-normal tracking-normal whitespace-normal break-words"
        >
          {text}
        </span>,
        document.body
      )}
    </span>
  );
};

// ── ConditionBadge: színkódolt állapot-chip, opcionális akku%-kal ────────────
// Használat: listakártya + termékoldal, konzisztensen a kép BAL FELSŐ sarkában.
//  - grade: nyers állapot-érték (office-kód vagy címke) → normalizeGrade
//  - battery: "92%" vagy 92 (opcionális; kiegészítőnél nem jelenítjük meg)
//  - category: a kiegészítő-szűréshez
export default function ConditionBadge({ grade, battery, category, className = '' }) {
  const g = normalizeGrade(grade);
  const batteryNum = battery == null ? null : parseInt(String(battery), 10);
  const showBattery = hasBatteryReading(category) && Number.isFinite(batteryNum);

  const label = g ? g.label : (grade ? String(grade) : 'Állapot');
  const color = g ? g.color : '#8A8A8A';
  const definition = g ? g.definition : 'Besorolás egyeztetés alatt – kérdezz minket bátran!';
  // A fokozat-magyarázat MINDIG a közös megjegyzéssel zárul (GRADE_COSMETIC_NOTE,
  // constants/grades.js). Így minden badge mellett ott van, EGY forrásból –
  // nem oldalanként duplikálva, ahol az egyiket frissítenénk, a másikat nem.
  const tipText = [
    showBattery && batteryNum === 100
      ? `${definition} A 100% jelentése: gyárilag ~100% akku-egészség.`
      : definition,
    GRADE_COSMETIC_NOTE,
  ].join(' ');

  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-white/95 border border-black/10 shadow-sm text-xs font-semibold ${className}`}
      style={{ color }}
      data-testid="condition-badge"
    >
      <span aria-hidden="true" className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: color }} />
      <span className="text-[#333333]">
        {label}
        {showBattery && <span className="font-medium text-gray-500"> · {batteryNum}%</span>}
      </span>
      <InfoTip text={tipText} label={`Mit jelent: ${label}?`} />
    </span>
  );
}
